import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useRouteContext } from "@tanstack/react-router";
import { Loader2, User } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
	FormField,
	Header,
	PhoneInput,
	ProfileAvatar,
	ProfileCard,
} from "@/app/partner/components";
import {
	formatPhoneForApi,
	parsePhoneFromApi,
	partnerDetailsQueryOptions,
	useUpdateAccountDetails,
	useUploadProfileImage,
} from "@/app/partner/pages/profile";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { isPartnerAdmin } from "@/lib/auth";

export const Route = createFileRoute("/flex/dashboard/profile")({
	component: ProfilePage,
});

function ProfilePage() {
	const { partner, isAgentAccess } = useRouteContext({ from: "__root__" });

	const partnerId = partner?.partner_details?.id || partner?.partner_id;
	const {
		data: partnerDetails,
		isLoading,
		error,
	} = useQuery({
		...partnerDetailsQueryOptions(partnerId as number),
		enabled: !!partnerId,
	});

	const updateAccountDetails = useUpdateAccountDetails();
	const uploadProfileImage = useUploadProfileImage();
	const avatarInputRef = useRef<HTMLInputElement>(null);

	const [isAccountEditing, setIsAccountEditing] = useState(false);
	const [isAddressEditing, setIsAddressEditing] = useState(false);
	const [isPersonalEditing, setIsPersonalEditing] = useState(false);

	// Form state
	const [accountName, setAccountName] = useState("");
	const [email, setEmail] = useState("");
	const [phone, setPhone] = useState("");
	const [countryCode, setCountryCode] = useState("+1");
	const [address, setAddress] = useState("");
	const [zipCode, setZipCode] = useState("");
	const [city, setCity] = useState("");
	const [state, setState] = useState("");
	const [country, setCountry] = useState("");
	const [avatarUrl, setAvatarUrl] = useState("");

	// Personal details for non-agent view
	const [fullName, setFullName] = useState("");
	const [personalEmail, setPersonalEmail] = useState("");
	const subPartnerRole =
		((partner?.set_profile_active?.[3]?.meta as Record<string, unknown>)
			?.sub_partner_role as string) || "agent";

	// Original values for cancel
	const [originalAccountData, setOriginalAccountData] = useState({
		accountName: "",
		email: "",
		phone: "",
		countryCode: "+1",
		avatarUrl: "",
	});
	const [originalAddressData, setOriginalAddressData] = useState({
		address: "",
		zipCode: "",
		city: "",
		state: "",
		country: "",
	});
	const [originalPersonalData, setOriginalPersonalData] = useState({
		fullName: "",
		personalEmail: "",
	});

	useEffect(() => {
		if (partnerDetails) {
			const addressData = partnerDetails.address;
			const formattedContact = partnerDetails.formatted_contact;
			const phoneDetails = parsePhoneFromApi(formattedContact);

			const name = partnerDetails.name || "";
			const emailVal = partnerDetails.data?.user_emails?.[0] || "";
			const phoneVal = phoneDetails.phone;
			const countryCodeVal = phoneDetails.countryCode;
			const addressVal = addressData?.line1 || "";
			const zipCodeVal = addressData?.zipcode || "";
			const cityVal = addressData?.city || "";
			const stateVal = addressData?.state || "";
			const countryVal = addressData?.country || "";
			const avatarVal = partnerDetails.images?.[0]?.path || "";

			setAccountName(name);
			setEmail(emailVal);
			setPhone(phoneVal);
			setCountryCode(countryCodeVal);
			setAddress(addressVal);
			setZipCode(zipCodeVal);
			setCity(cityVal);
			setState(stateVal);
			setCountry(countryVal);
			setAvatarUrl(avatarVal);

			// Personal details from user
			const userName = partner?.user_details?.name || partner?.name || "";
			const userEmail = partner?.user_details?.email || partner?.email || "";
			setFullName(userName);
			setPersonalEmail(userEmail);

			setOriginalAccountData({
				accountName: name,
				email: emailVal,
				phone: phoneVal,
				countryCode: countryCodeVal,
				avatarUrl: avatarVal,
			});
			setOriginalAddressData({
				address: addressVal,
				zipCode: zipCodeVal,
				city: cityVal,
				state: stateVal,
				country: countryVal,
			});
			setOriginalPersonalData({
				fullName: userName,
				personalEmail: userEmail,
			});
		}
	}, [partnerDetails, partner]);

	const isAdmin = isPartnerAdmin(partner);
	const hasEditAccess = isAgentAccess || isAdmin;

	const handleAccountEdit = () => setIsAccountEditing(true);
	const handleAccountSave = async () => {
		if (!partnerId) return;

		await updateAccountDetails.mutateAsync({
			partnerId,
			data: {
				partner: {
					address: {
						line1: address,
						city: city,
						state: state,
						country: country,
						zipcode: zipCode,
						contact_number: formatPhoneForApi(countryCode, phone),
					},
					images: avatarUrl ? [{ path: avatarUrl }] : undefined,
				},
			},
		});
		setIsAccountEditing(false);
		setOriginalAccountData({
			accountName,
			email,
			phone,
			countryCode,
			avatarUrl,
		});
	};
	const handleAccountCancel = () => {
		setIsAccountEditing(false);
		setAccountName(originalAccountData.accountName);
		setEmail(originalAccountData.email);
		setPhone(originalAccountData.phone);
		setCountryCode(originalAccountData.countryCode);
		setAvatarUrl(originalAccountData.avatarUrl);
	};

	const handleAddressEdit = () => setIsAddressEditing(true);
	const handleAddressSave = async () => {
		if (!partnerId) return;

		await updateAccountDetails.mutateAsync({
			partnerId,
			data: {
				partner: {
					address: {
						line1: address,
						city: city,
						state: state,
						country: country,
						zipcode: zipCode,
						contact_number: formatPhoneForApi(countryCode, phone),
					},
				},
			},
		});
		setIsAddressEditing(false);
		setOriginalAddressData({
			address,
			zipCode,
			city,
			state,
			country,
		});
	};
	const handleAddressCancel = () => {
		setIsAddressEditing(false);
		setAddress(originalAddressData.address);
		setZipCode(originalAddressData.zipCode);
		setCity(originalAddressData.city);
		setState(originalAddressData.state);
		setCountry(originalAddressData.country);
	};

	const handlePersonalCancel = () => {
		setIsPersonalEditing(false);
		setFullName(originalPersonalData.fullName);
		setPersonalEmail(originalPersonalData.personalEmail);
	};

	const inputClasses = (isEditing: boolean) =>
		isEditing
			? "min-h-[44px] bg-white border-neutral-400 ring-[3px] ring-neutral-400/50 shadow-xs"
			: "min-h-[44px] bg-white border-neutral-200 shadow-xs";

	if (isLoading) {
		return (
			<div className="h-full flex flex-col bg-white">
				<Header
					title="PROFILE"
					icon={<User className="size-4 text-gray-500" />}
				/>
				<div className="flex-1 flex items-center justify-center">
					<Loader2 className="size-8 animate-spin text-blue-600" />
				</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className="h-full flex flex-col bg-white">
				<Header
					title="PROFILE"
					icon={<User className="size-4 text-gray-500" />}
				/>
				<div className="flex-1 flex items-center justify-center">
					<p className="text-red-500">Failed to load profile data</p>
				</div>
			</div>
		);
	}

	// Agent view OR admin partner - show full profile with avatar, phone, address
	if (isAgentAccess || hasEditAccess) {
		return (
			<div className="h-full flex flex-col bg-white">
				<Header
					title="PROFILE"
					icon={<User className="size-4 text-gray-500" />}
				/>

				<div className="flex-1 overflow-auto p-4 md:p-6">
					<div className="flex max-w-full flex-col gap-4 md:gap-10">
						<ProfileCard
							title="Account Details"
							isEditing={isAccountEditing}
							onEdit={hasEditAccess ? handleAccountEdit : undefined}
							onSave={handleAccountSave}
							onCancel={handleAccountCancel}
						>
							<ProfileAvatar
								src={avatarUrl}
								alt={accountName}
								isEditing={isAccountEditing}
								onEdit={() => avatarInputRef.current?.click()}
							/>
							<input
								ref={avatarInputRef}
								type="file"
								accept="image/png,image/jpeg,image/jpg"
								className="hidden"
								onChange={(e) => {
									const file = e.target.files?.[0];
									if (file) {
										uploadProfileImage.mutate(
											{ file },
											{
												onSuccess: (url) => setAvatarUrl(url),
											},
										);
									}
									e.target.value = "";
								}}
							/>

							<div className="grid w-full grid-cols-1 gap-4 md:grid-cols-2 md:gap-6">
								<FormField label="Account Name">
									<Input
										value={accountName}
										onChange={(e) => setAccountName(e.target.value)}
										disabled={!isAccountEditing}
										className={inputClasses(isAccountEditing)}
									/>
								</FormField>

								<FormField label="Account Email Address">
									<Input
										type="email"
										value={email}
										onChange={(e) => setEmail(e.target.value)}
										disabled={!isAccountEditing}
										className={inputClasses(isAccountEditing)}
									/>
								</FormField>

								<FormField label="Phone Number">
									<PhoneInput
										value={phone}
										countryCode={countryCode}
										onChange={(value) => setPhone(value)}
										onCountryChange={(code) => setCountryCode(code)}
										isEditing={isAccountEditing}
										disabled={!isAccountEditing}
									/>
								</FormField>
							</div>
						</ProfileCard>

						<ProfileCard
							title="Address Information"
							isEditing={isAddressEditing}
							onEdit={hasEditAccess ? handleAddressEdit : undefined}
							onSave={handleAddressSave}
							onCancel={handleAddressCancel}
						>
							<div className="w-full">
								<FormField label="Address" className="min-w-full">
									<Textarea
										value={address}
										onChange={(e) => setAddress(e.target.value)}
										disabled={!isAddressEditing}
										className="min-h-[94px] bg-white border-neutral-200 shadow-xs resize-none"
									/>
								</FormField>
							</div>

							{/* Figma `pair` 2174:20692 / 2174:20701 — Zip/City and
							    State/Country stay side by side on mobile, 157px each
							    with a 12px gutter. */}
							<div className="grid w-full grid-cols-2 gap-3 md:gap-6">
								<FormField label="Zip Code">
									<Input
										value={zipCode}
										onChange={(e) => setZipCode(e.target.value)}
										disabled={!isAddressEditing}
										className={inputClasses(isAddressEditing)}
									/>
								</FormField>

								<FormField label="City">
									<Input
										value={city}
										onChange={(e) => setCity(e.target.value)}
										disabled={!isAddressEditing}
										className={inputClasses(isAddressEditing)}
									/>
								</FormField>

								<FormField label="State">
									<Input
										value={state}
										onChange={(e) => setState(e.target.value)}
										disabled={!isAddressEditing}
										className={inputClasses(isAddressEditing)}
									/>
								</FormField>

								<FormField label="Country">
									<Input
										value={country}
										onChange={(e) => setCountry(e.target.value)}
										disabled={!isAddressEditing}
										className={inputClasses(isAddressEditing)}
									/>
								</FormField>
							</div>
						</ProfileCard>
					</div>
				</div>
			</div>
		);
	}

	// Non-agent view - show Personal Details + Account Details (no phone, no address)
	const formatRole = (role: string) => {
		return role
			.split("_")
			.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
			.join(" ");
	};

	return (
		<div className="h-full flex flex-col bg-white">
			<Header
				title="PROFILE"
				icon={<User className="size-4 text-gray-500" />}
			/>

			<div className="flex-1 overflow-auto p-4 md:p-6">
				<div className="flex max-w-full flex-col gap-4 md:gap-10">
					{/* Personal Details */}
					<ProfileCard
						title="Personal Details"
						isEditing={isPersonalEditing}
						onCancel={handlePersonalCancel}
					>
						<div className="grid w-full grid-cols-1 gap-4 md:grid-cols-2 md:gap-6">
							<FormField label="Full Name" required>
								<Input
									value={fullName}
									onChange={(e) => setFullName(e.target.value)}
									disabled={!isPersonalEditing}
									className={inputClasses(isPersonalEditing)}
								/>
							</FormField>

							<FormField label="Email Address" required>
								<Input
									type="email"
									value={personalEmail}
									onChange={(e) => setPersonalEmail(e.target.value)}
									disabled={!isPersonalEditing}
									className={inputClasses(isPersonalEditing)}
								/>
							</FormField>
						</div>

						<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
							<FormField label="Sub Partner Role" required>
								<Input
									value={formatRole(subPartnerRole)}
									disabled
									className="min-h-[44px] bg-gray-50 border-neutral-200 shadow-xs"
								/>
							</FormField>
						</div>
					</ProfileCard>

					{/* Account Details */}
					<ProfileCard title="Account Details">
						<div className="grid w-full grid-cols-1 gap-4 md:grid-cols-2 md:gap-6">
							<FormField label="Account Name">
								<Input
									value={accountName}
									disabled
									className="min-h-[44px] bg-gray-50 border-neutral-200 shadow-xs"
								/>
							</FormField>

							<FormField label="Account Email Address">
								<Input
									type="email"
									value={email}
									disabled
									className="min-h-[44px] bg-gray-50 border-neutral-200 shadow-xs"
								/>
							</FormField>
						</div>
					</ProfileCard>
				</div>
			</div>
		</div>
	);
}
